import { useCallback, useMemo, useState } from "react";
import {
  HighlightedMapElement,
  MapInspector,
  UIContextType,
  WeatherMapLayers,
  MapDimensions,
} from ".";
import { GM_Point } from "../../shared-types/WeatherTypes";
import { SpotterWave } from "../../shared-types/SpotterTypes";

// import { AnalyticsEvent } from "../Analytics";
import { ServiceWorkerStatus } from "../../service-worker/register";
import { useAppUrl } from "../../helpers/use-app-url";
import { getSingleSpotterWaveData } from "../../helpers/use-spotter-data";
import { usePanelState } from "../PanelContext";
import AsyncWeatherProviderProxy from "web-workers/weather-worker/AsyncWeatherProviderProxy";

export type UiStateProps = {
  serviceWorkerStatus?: ServiceWorkerStatus;
  // trackAnalyticsEvent: (
  //   type: AnalyticsEvent,
  //   action: string,
  //   eventData?: Record<string, any>
  // ) => void;
  getRequestTimes: AsyncWeatherProviderProxy["getRequestTimes"];
};

export const useUIState = ({
  serviceWorkerStatus,
  // trackAnalyticsEvent,
  getRequestTimes,
}: UiStateProps) => {
  const [highlightedMapElements, setHighlightedMapElements] = useState<
    HighlightedMapElement[]
  >([]);
  const [activeMapInspector, setActiveMapInspector] =
    useState<MapInspector | null>(null);

  const [mapDimensions, setMapDimensions] = useState<MapDimensions>({
    width: 0,
    height: 0,
  });

  /**
   * The weather layers selection is stored in the query string.
   * We use @see useQueryParams for the handling of this.
   */
  const {
    weatherMapLayers: qsWeatherMapLayers,
    setWeatherMapLayers: setQSetWeatherMapLayers,
  } = useAppUrl();
  // const { getRequestTimes } = useContext(WeatherContext);

  const setWeatherMapLayersWithAnalytics = useCallback(
    (layers: WeatherMapLayers) => {
      setQSetWeatherMapLayers(layers);
      // trackAnalyticsEvent(
      //   AnalyticsEvent.VisualizationTypeChanged,
      //   `raster: ${layers.raster}, vector: ${
      //     layers.vector || "Off"
      //   }, show spotters: ${layers.showSpotters}`,
      //   { layers }
      // );
    },
    [setQSetWeatherMapLayers /*, trackAnalyticsEvent */]
  );

  // convenience methods for checking highlight state
  const isSpotterHighlighted = useCallback(
    (id: string) =>
      !!activeMapInspector &&
      !!highlightedMapElements.find((e) => e.type === "spotter" && e.id === id),
    [highlightedMapElements, activeMapInspector]
  );

  const [activeSpotterData, setActiveSpotterData] = useState<SpotterWave[]>();
  useMemo(() => {
    if (activeMapInspector && activeMapInspector?.type === "spotter") {
      getSingleSpotterWaveData(
        activeMapInspector.spotterId,
        getRequestTimes
      ).then((results) => {
        setActiveSpotterData(results?.data?.waves);
      });
    }
  }, [activeMapInspector, getRequestTimes]);

  const inspectorCallbacks = useMemo(
    () => ({
      inspectSpotter: (spotterId: string) => {
        // Highlight and inspect Spotter
        setHighlightedMapElements([{ type: "spotter", id: spotterId }]);
        setActiveMapInspector({ type: "spotter", spotterId });
        // trackAnalyticsEvent(AnalyticsEvent.SpotterClicked, spotterId, {
        //   spotterId,
        // });
      },
      inspectWeather: (position: GM_Point) => {
        setHighlightedMapElements([]);
        setActiveMapInspector({ type: "weather", position });
        // trackAnalyticsEvent(
        //   AnalyticsEvent.WeatherClicked,
        //   `lat: ${position.lat}, lon: ${position.lon}`,
        //   { position }
        // );
      },
      clearMapInspector: () => {
        setActiveMapInspector(null);
        setActiveSpotterData(undefined);
      },
      clearHighlightedMapElements: () => {
        setHighlightedMapElements([]);
      },
    }),
    [
      setActiveMapInspector,
      // getHistoricalSpotterData,
      setHighlightedMapElements,
      // trackAnalyticsEvent,
    ]
  );

  const [isLoading, setIsLoading] = useState(false);

  /** Use the `usePanelState` hook to create state management values for each
   *  of our panels. This hook gives us callbacks we can pass down through the
   *  `PanelContext` so panels can receive their current visibility without
   *  having to drill it down as a prop. The state value also includes callbacks
   *  that let the panel trigger visibility changes from within the component.
   */
  const [panelLayoutMounted, setPanelLayoutMounted] = useState<boolean>(false);
  const sidebarPanelState = usePanelState(
    "minimized",
    0.3, // seconds for transition
    true,
    panelLayoutMounted
  );
  const timelinePanelState = usePanelState(
    "open",
    0,
    false,
    panelLayoutMounted
  );
  const mapOverlayPanelState = usePanelState(
    "open",
    0,
    false,
    panelLayoutMounted
  );

  return useMemo<UIContextType>(
    () => ({
      ...inspectorCallbacks,

      isSpotterHighlighted,
      activeSpotterData,
      activeMapInspector,
      highlightedMapElements,
      mapDimensions,
      setMapDimensions,

      weatherMapLayers: qsWeatherMapLayers,
      setWeatherMapLayers: setWeatherMapLayersWithAnalytics,

      serviceWorkerStatus,

      isLoading,
      setIsLoading,
      sidebarPanelState,
      timelinePanelState,
      mapOverlayPanelState,
      setPanelLayoutMounted,
    }),
    [
      inspectorCallbacks,
      isSpotterHighlighted,
      activeMapInspector,
      activeSpotterData,
      mapOverlayPanelState,
      highlightedMapElements,
      mapDimensions,
      setMapDimensions,
      qsWeatherMapLayers,
      setWeatherMapLayersWithAnalytics,
      serviceWorkerStatus,
      isLoading,
      sidebarPanelState,
      timelinePanelState,
    ]
  );
};
